import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart, Line, PieChart, Pie, Cell, Area, AreaChart, ComposedChart
} from 'recharts';
import {
    TrendingUp, TrendingDown, Users, Eye, MousePointerClick, Globe, MapPin,
    Monitor, Smartphone, Tablet, Download, FileText, FileSpreadsheet,
    Presentation, RefreshCw, Sparkles, ChevronDown, ChevronUp, AlertCircle,
    Activity, BarChart3, ArrowUpRight, ArrowDownRight, Minus, Loader2,
    Target, Zap, Clock, CheckCircle, Info, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/* ─────────────────── Color Palette ─────────────────── */
const COLORS = {
    emerald: '#059669',
    emeraldLight: '#10b981',
    emeraldDark: '#047857',
    blue: '#3b82f6',
    purple: '#8b5cf6',
    orange: '#f59e0b',
    pink: '#ec4899',
    teal: '#14b8a6',
    indigo: '#6366f1',
    rose: '#f43f5e',
    slate: '#64748b',
    cyan: '#06b6d4',
};

const CHART_COLORS = [
    COLORS.emerald, COLORS.blue, COLORS.purple, COLORS.orange,
    COLORS.pink, COLORS.teal, COLORS.indigo, COLORS.rose,
    COLORS.cyan, COLORS.slate,
];

const SOURCE_COLORS = {
    'Instagram': '#E4405F',
    'Google Organic': '#4285F4',
    'Direct': '#64748b',
    'WhatsApp / WL Referral': '#25D366',
    'TikTok': '#000000',
    'Facebook': '#1877F2',
    'Google Ads': '#FBBC04',
    'Other Search': '#8b5cf6',
    'Other': '#94a3b8',
};

/* ─────────────────── Animated Counter ─────────────────── */
const AnimatedNumber = ({ value, duration = 1200, prefix = '', suffix = '', decimals = 0 }) => {
    const [display, setDisplay] = useState(0);
    useEffect(() => {
        if (typeof value !== 'number' || isNaN(value)) { setDisplay(0); return; }
        let start = 0;
        const step = value / (duration / 16);
        let current = 0;
        const timer = setInterval(() => {
            current += step;
            if (current >= value) { setDisplay(value); clearInterval(timer); }
            else { setDisplay(current); }
        }, 16);
        return () => clearInterval(timer);
    }, [value, duration]);

    const formatted = decimals > 0
        ? display.toFixed(decimals)
        : Math.round(display).toLocaleString('id-ID');

    return <span>{prefix}{formatted}{suffix}</span>;
};

/* ─────────────────── Change Badge ─────────────────── */
const ChangeBadge = ({ value }) => {
    if (value === undefined || value === null) return null;
    const isPositive = value > 0;
    const isNeutral = value === 0;
    const Icon = isPositive ? ArrowUpRight : isNeutral ? Minus : ArrowDownRight;
    const colorClass = isPositive
        ? 'text-emerald-600 bg-emerald-50'
        : isNeutral
            ? 'text-gray-500 bg-gray-50'
            : 'text-red-500 bg-red-50';

    return (
        <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
            <Icon className="w-3 h-3" />
            {Math.abs(value)}%
        </span>
    );
};

/* ─────────────────── Priority Badge ─────────────────── */
const PriorityBadge = ({ priority }) => {
    const colors = {
        high: 'bg-red-100 text-red-700 border-red-200',
        medium: 'bg-amber-100 text-amber-700 border-amber-200',
        low: 'bg-blue-100 text-blue-700 border-blue-200',
    };
    return (
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${colors[priority] || colors.medium}`}>
            {(priority || 'medium').toUpperCase()}
        </span>
    );
};

/* ─────────────────── Custom Tooltip ─────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
        <div style={{
            background: 'rgba(255,255,255,0.96)',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '12px 16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            backdropFilter: 'blur(8px)',
        }}>
            <p style={{ fontWeight: 600, color: '#1a1a2e', fontSize: 13, marginBottom: 6 }}>{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.color, fontSize: 12, margin: '3px 0' }}>
                    {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString('id-ID') : p.value}</strong>
                </p>
            ))}
        </div>
    );
};

/* ═══════════════════ MAIN COMPONENT ═══════════════════ */
const PerformanceReport = () => {
    const { getToken } = useAuth();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('last_30');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // AI
    const [aiData, setAiData] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [expandedSections, setExpandedSections] = useState({});

    // Export
    const [exportLoading, setExportLoading] = useState({});

    /* ─── Fetch Report Data ─── */
    const fetchReport = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { period };
            if (period === 'custom' && startDate && endDate) {
                params.start_date = startDate;
                params.end_date = endDate;
            }
            const res = await axios.get(`${API_URL}/admin/performance-report`, {
                headers: { Authorization: `Bearer ${getToken()}` },
                params,
            });
            setData(res.data);
        } catch (err) {
            console.error('Report fetch error:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load report');
        } finally {
            setIsLoading(false);
        }
    }, [period, startDate, endDate, getToken]);

    useEffect(() => {
        if (period !== 'custom' || (startDate && endDate)) {
            fetchReport();
        }
    }, [fetchReport, period, startDate, endDate]);

    /* ─── Fetch AI Summary ─── */
    const fetchAISummary = async () => {
        setAiLoading(true);
        try {
            const params = { period };
            if (period === 'custom' && startDate && endDate) {
                params.start_date = startDate;
                params.end_date = endDate;
            }
            const res = await axios.get(`${API_URL}/admin/performance-report/ai-summary`, {
                headers: { Authorization: `Bearer ${getToken()}` },
                params,
            });
            setAiData(res.data);
        } catch (err) {
            console.error('AI summary error:', err);
        } finally {
            setAiLoading(false);
        }
    };

    /* ─── Export ─── */
    const handleExport = async (format) => {
        setExportLoading(prev => ({ ...prev, [format]: true }));
        try {
            const params = { format, period, include_ai: true };
            if (period === 'custom' && startDate && endDate) {
                params.start_date = startDate;
                params.end_date = endDate;
            }
            const res = await axios.get(`${API_URL}/admin/performance-report/export`, {
                headers: { Authorization: `Bearer ${getToken()}` },
                params,
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            const ext = format === 'pptx' ? 'pptx' : format === 'xlsx' ? 'xlsx' : 'pdf';
            a.download = `spencer_report_${period}.${ext}`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export error:', err);
        } finally {
            setExportLoading(prev => ({ ...prev, [format]: false }));
        }
    };

    /* ─── Toggle section expand ─── */
    const toggleSection = (idx) => {
        setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    /* ═════════════ RENDER ═════════════ */

    // Loading State
    if (isLoading && !data) {
        return (
            <div className="flex flex-col items-center justify-center h-96 space-y-4">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-emerald-200 rounded-full animate-spin border-t-emerald-600" />
                    <BarChart3 className="w-6 h-6 text-emerald-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </div>
                <p className="text-gray-500 font-medium animate-pulse">Loading Performance Report...</p>
            </div>
        );
    }

    // Not Configured State
    if (data && data.configured === false) {
        return (
            <div className="max-w-2xl mx-auto mt-16">
                <Card>
                    <CardContent className="p-8 text-center space-y-4">
                        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                            <AlertCircle className="w-8 h-8 text-amber-600" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">GA4 Not Configured</h3>
                        <p className="text-gray-500">{data.message || 'Please configure Google Analytics 4 to use this feature.'}</p>
                        <div className="bg-gray-50 rounded-lg p-4 text-left text-sm space-y-2">
                            <p className="font-semibold text-gray-700">Setup Steps:</p>
                            <ol className="list-decimal list-inside text-gray-600 space-y-1">
                                <li>Create a Google Cloud Service Account</li>
                                <li>Grant it "Viewer" access on your GA4 property</li>
                                <li>Place the JSON key file at <code className="bg-gray-200 px-1 rounded">backend/credentials/</code></li>
                                <li>Set <code className="bg-gray-200 px-1 rounded">GA4_PROPERTY_ID</code> in .env</li>
                            </ol>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Error State
    if (error && !data) {
        return (
            <div className="max-w-2xl mx-auto mt-16">
                <Card>
                    <CardContent className="p-8 text-center space-y-4">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                            <AlertCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Error Loading Report</h3>
                        <p className="text-gray-500">{error}</p>
                        <Button onClick={fetchReport}>Retry</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (data && data.overview && data.overview.error) {
        return (
            <div className="max-w-2xl mx-auto mt-16">
                <Card>
                    <CardContent className="p-8 text-center space-y-4">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                            <AlertCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">GA4 Permission Error</h3>
                        <p className="text-gray-500">{data.overview.error}</p>
                        <Button onClick={fetchReport}>Retry</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!data) return null;

    const overview = data.overview || {};
    const comparison = data.comparison || {};
    const trafficTrend = data.traffic_trend || [];
    const trafficSources = data.traffic_sources || {};
    const landingPages = data.landing_pages || [];
    const pagesScreens = data.pages_screens || [];
    const events = data.events || {};
    const devices = data.devices || [];
    const location = data.location || {};
    const funnel = data.funnel || [];
    const clarity = data.clarity;
    const periodInfo = data.period || {};

    const PERIOD_OPTIONS = [
        { value: 'this_week', label: 'This Week' },
        { value: 'last_week', label: 'Last Week' },
        { value: 'this_month', label: 'This Month' },
        { value: 'last_month', label: 'Last Month' },
        { value: 'last_7', label: 'Last 7 Days' },
        { value: 'last_30', label: 'Last 30 Days' },
        { value: 'last_90', label: 'Last 90 Days' },
        { value: 'custom', label: 'Custom Range' },
    ];

    return (
        <div className="space-y-6 pb-12">
            {/* ═══ HEADER ═══ */}
            <div style={{
                background: 'linear-gradient(135deg, #064e3b 0%, #047857 50%, #059669 100%)',
                borderRadius: '16px',
                padding: '28px 32px',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Decorative circles */}
                <div style={{
                    position: 'absolute', top: -40, right: -40, width: 160, height: 160,
                    borderRadius: '50%', background: 'rgba(255,255,255,0.06)',
                }} />
                <div style={{
                    position: 'absolute', bottom: -30, right: 80, width: 100, height: 100,
                    borderRadius: '50%', background: 'rgba(255,255,255,0.04)',
                }} />

                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 relative z-10">
                    <div>
                        <h2 className="text-2xl xl:text-3xl font-bold text-white flex items-center gap-3">
                            <BarChart3 className="w-7 h-7" />
                            Website Performance Report
                        </h2>
                        <p className="text-emerald-200 mt-1 text-sm">
                            {periodInfo.start && periodInfo.end
                                ? `${periodInfo.start} — ${periodInfo.end}`
                                : 'Powered by Google Analytics 4'}
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        {/* Period Selector */}
                        <select
                            value={period}
                            onChange={(e) => { setPeriod(e.target.value); setStartDate(''); setEndDate(''); }}
                            className="bg-white/10 text-white border border-white/20 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white/30 backdrop-blur-sm"
                            style={{ minWidth: 150 }}
                        >
                            {PERIOD_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value} style={{ color: '#1a1a2e' }}>{opt.label}</option>
                            ))}
                        </select>

                        {period === 'custom' && (
                            <div className="flex items-center gap-2">
                                <input
                                    type="date" value={startDate}
                                    onChange={(e) => { setStartDate(e.target.value); setPeriod('custom'); }}
                                    className="bg-white/10 text-white border border-white/20 rounded-lg px-2 py-2 text-sm outline-none"
                                />
                                <span className="text-white/60 text-sm">—</span>
                                <input
                                    type="date" value={endDate}
                                    onChange={(e) => { setEndDate(e.target.value); setPeriod('custom'); }}
                                    className="bg-white/10 text-white border border-white/20 rounded-lg px-2 py-2 text-sm outline-none"
                                />
                            </div>
                        )}

                        {/* Export Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => handleExport('pdf')}
                                disabled={exportLoading.pdf}
                                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-sm transition-all border border-white/20 disabled:opacity-50"
                                title="Export PDF"
                            >
                                {exportLoading.pdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                                PDF
                            </button>
                            <button
                                onClick={() => handleExport('pptx')}
                                disabled={exportLoading.pptx}
                                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-sm transition-all border border-white/20 disabled:opacity-50"
                                title="Export PowerPoint"
                            >
                                {exportLoading.pptx ? <Loader2 className="w-4 h-4 animate-spin" /> : <Presentation className="w-4 h-4" />}
                                PPT
                            </button>
                            <button
                                onClick={() => handleExport('xlsx')}
                                disabled={exportLoading.xlsx}
                                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-sm transition-all border border-white/20 disabled:opacity-50"
                                title="Export Excel"
                            >
                                {exportLoading.xlsx ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                                Excel
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Loading overlay for re-fetch */}
            {isLoading && data && (
                <div className="fixed top-4 right-4 z-50 bg-white shadow-lg rounded-full px-4 py-2 flex items-center gap-2 border">
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                    <span className="text-sm text-gray-600">Updating...</span>
                </div>
            )}

            {/* ═══ SECTION 1: KPI CARDS ═══ */}
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                {[
                    {
                        title: 'Users', value: overview.users || 0, key: 'users',
                        icon: Users, gradient: 'from-blue-500 to-blue-600',
                        iconBg: 'bg-blue-100', iconColor: 'text-blue-600'
                    },
                    {
                        title: 'Sessions', value: overview.sessions || 0, key: 'sessions',
                        icon: Activity, gradient: 'from-emerald-500 to-emerald-600',
                        iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600'
                    },
                    {
                        title: 'Page Views', value: overview.page_views || 0, key: 'page_views',
                        icon: Eye, gradient: 'from-purple-500 to-purple-600',
                        iconBg: 'bg-purple-100', iconColor: 'text-purple-600'
                    },
                    {
                        title: 'Engagement Rate', value: overview.engagement_rate || 0, key: 'engagement_rate',
                        icon: Target, gradient: 'from-amber-500 to-orange-500',
                        iconBg: 'bg-amber-100', iconColor: 'text-amber-600',
                        suffix: '%', decimals: 1
                    },
                ].map((kpi, idx) => {
                    const Icon = kpi.icon;
                    const comp = comparison[kpi.key];
                    return (
                        <Card key={idx} className="relative overflow-hidden border-0 shadow-md hover:shadow-lg transition-shadow duration-300" style={{ animationDelay: `${idx * 100}ms` }}>
                            <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${kpi.gradient}`} />
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500 mb-1">{kpi.title}</p>
                                        <p className="text-2xl xl:text-3xl font-bold text-gray-900">
                                            <AnimatedNumber
                                                value={kpi.value}
                                                suffix={kpi.suffix || ''}
                                                decimals={kpi.decimals || 0}
                                            />
                                        </p>
                                        {comp && (
                                            <div className="mt-2">
                                                <ChangeBadge value={comp.change_pct} />
                                                <span className="text-xs text-gray-400 ml-1.5">vs prev</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className={`p-2.5 rounded-xl ${kpi.iconBg}`}>
                                        <Icon className={`w-5 h-5 ${kpi.iconColor}`} />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Secondary KPIs */}
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                {[
                    { title: 'New Users', value: overview.new_users || 0, key: 'new_users', icon: Users },
                    { title: 'Bounce Rate', value: overview.bounce_rate || 0, key: 'bounce_rate', icon: TrendingDown, suffix: '%', decimals: 1 },
                    { title: 'Avg Duration', value: overview.avg_session_duration || 0, icon: Clock, suffix: 's', decimals: 1 },
                    { title: 'Sessions/User', value: overview.sessions_per_user || 0, icon: Zap, decimals: 2 },
                ].map((kpi, idx) => {
                    const Icon = kpi.icon;
                    const comp = comparison[kpi.key];
                    return (
                        <Card key={idx} className="border shadow-sm hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{kpi.title}</p>
                                        <p className="text-xl font-bold text-gray-800 mt-1">
                                            <AnimatedNumber value={kpi.value} suffix={kpi.suffix || ''} decimals={kpi.decimals || 0} />
                                        </p>
                                    </div>
                                    <Icon className="w-4 h-4 text-gray-300" />
                                </div>
                                {comp && <div className="mt-1"><ChangeBadge value={comp.change_pct} /></div>}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* ═══ SECTION 2: TRAFFIC TREND ═══ */}
            <Card className="border-0 shadow-md">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        Traffic Trend
                    </CardTitle>
                    <CardDescription>Daily users, sessions, and engagement</CardDescription>
                </CardHeader>
                <CardContent>
                    {trafficTrend.length > 0 ? (
                        <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height={350}>
                            <AreaChart data={trafficTrend}>
                                <defs>
                                    <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.15} />
                                        <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={COLORS.emerald} stopOpacity={0.15} />
                                        <stop offset="95%" stopColor={COLORS.emerald} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                <XAxis
                                    dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }}
                                    tickFormatter={(v) => { try { const d = new Date(v); return `${d.getDate()}/${d.getMonth() + 1}`; } catch { return v; } }}
                                />
                                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                                <Area type="monotone" dataKey="totalUsers" name="Users" stroke={COLORS.blue} fill="url(#gradUsers)" strokeWidth={2.5} dot={false} />
                                <Area type="monotone" dataKey="sessions" name="Sessions" stroke={COLORS.emerald} fill="url(#gradSessions)" strokeWidth={2.5} dot={false} />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-48 text-gray-400">
                            <p>No trend data available for this period</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ═══ SECTION 3: TRAFFIC SOURCES ═══ */}
            <div className="grid gap-4 lg:grid-cols-5">
                {/* Donut Chart */}
                <Card className="lg:col-span-2 border-0 shadow-md">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Globe className="w-5 h-5 text-blue-600" />
                            Traffic Sources
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {(trafficSources.grouped || []).length > 0 ? (
                            <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={trafficSources.grouped}
                                        cx="50%" cy="50%"
                                        innerRadius={65} outerRadius={110}
                                        paddingAngle={3}
                                        dataKey="sessions"
                                        nameKey="name"
                                        stroke="none"
                                    >
                                        {(trafficSources.grouped || []).map((entry, i) => (
                                            <Cell key={i} fill={SOURCE_COLORS[entry.name] || CHART_COLORS[i % CHART_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend
                                        layout="vertical" align="right" verticalAlign="middle"
                                        iconType="circle" iconSize={8}
                                        wrapperStyle={{ fontSize: 11, lineHeight: '20px' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No data</div>
                        )}
                    </CardContent>
                </Card>

                {/* Source Table */}
                <Card className="lg:col-span-3 border-0 shadow-md">
                    <CardHeader>
                        <CardTitle className="text-lg">Source Details</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-gray-50/80">
                                        <th className="text-left px-4 py-3 font-semibold text-gray-600">Source</th>
                                        <th className="text-right px-4 py-3 font-semibold text-gray-600">Users</th>
                                        <th className="text-right px-4 py-3 font-semibold text-gray-600">Sessions</th>
                                        <th className="text-right px-4 py-3 font-semibold text-gray-600">Engagement</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(trafficSources.grouped || []).map((src, i) => (
                                        <tr key={i} className="border-b last:border-0 hover:bg-gray-50/50 transition-colors">
                                            <td className="px-4 py-3 font-medium text-gray-800 flex items-center gap-2">
                                                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: SOURCE_COLORS[src.name] || CHART_COLORS[i % CHART_COLORS.length] }} />
                                                {src.name}
                                            </td>
                                            <td className="text-right px-4 py-3 text-gray-600">{(src.users || 0).toLocaleString('id-ID')}</td>
                                            <td className="text-right px-4 py-3 text-gray-600">{(src.sessions || 0).toLocaleString('id-ID')}</td>
                                            <td className="text-right px-4 py-3 text-gray-600">{src.engagement_rate || 0}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* ═══ SECTION 4: TOP PAGES ═══ */}
            <Card className="border-0 shadow-md">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <FileText className="w-5 h-5 text-purple-600" />
                        Top Landing Pages
                    </CardTitle>
                    <CardDescription>Internal pages (/admin, /login) are automatically excluded</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-gray-50/80">
                                    <th className="text-left px-4 py-3 font-semibold text-gray-600">#</th>
                                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Page</th>
                                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Sessions</th>
                                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Users</th>
                                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Bounce %</th>
                                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Avg Duration</th>
                                </tr>
                            </thead>
                            <tbody>
                                {landingPages.slice(0, 15).map((page, i) => {
                                    const path = page.landingPagePlusQueryString || page.pagePath || 'N/A';
                                    return (
                                        <tr key={i} className="border-b last:border-0 hover:bg-gray-50/50">
                                            <td className="px-4 py-3 text-gray-400 font-mono text-xs">{i + 1}</td>
                                            <td className="px-4 py-3 font-medium text-gray-800 max-w-xs truncate" title={path}>{path}</td>
                                            <td className="text-right px-4 py-3 text-gray-600">{(page.sessions || 0).toLocaleString('id-ID')}</td>
                                            <td className="text-right px-4 py-3 text-gray-600">{(page.totalUsers || 0).toLocaleString('id-ID')}</td>
                                            <td className="text-right px-4 py-3">
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${(page.bounce_rate || 0) > 70 ? 'bg-red-50 text-red-600' : (page.bounce_rate || 0) > 50 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                                                    {page.bounce_rate || 0}%
                                                </span>
                                            </td>
                                            <td className="text-right px-4 py-3 text-gray-600">{page.avg_duration || 0}s</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {landingPages.length === 0 && (
                            <div className="py-12 text-center text-gray-400">No page data available</div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* ═══ SECTION 5: BOOKING FUNNEL ═══ */}
            <Card className="border-0 shadow-md">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <Target className="w-5 h-5 text-emerald-600" />
                        Booking Funnel
                    </CardTitle>
                    <CardDescription>User journey from viewing rooms to successful booking</CardDescription>
                </CardHeader>
                <CardContent>
                    {funnel.length > 0 ? (
                        <div className="space-y-3">
                            {funnel.map((step, i) => {
                                const maxCount = Math.max(...funnel.map(f => f.count || 0), 1);
                                const widthPct = Math.max(((step.count || 0) / maxCount) * 100, 8);
                                const gradient = [
                                    'from-emerald-400 to-emerald-500',
                                    'from-blue-400 to-blue-500',
                                    'from-purple-400 to-purple-500',
                                    'from-orange-400 to-orange-500',
                                    'from-rose-400 to-rose-500',
                                ][i] || 'from-gray-400 to-gray-500';

                                return (
                                    <div key={i} className="group">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-semibold text-gray-700">{step.name}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm font-bold text-gray-900">{(step.count || 0).toLocaleString('id-ID')}</span>
                                                {i > 0 && step.drop_off_pct > 0 && (
                                                    <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-full font-medium">
                                                        -{step.drop_off_pct}% drop
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-8 overflow-hidden">
                                            <div
                                                className={`h-full bg-gradient-to-r ${gradient} rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-3`}
                                                style={{ width: `${widthPct}%` }}
                                            >
                                                {widthPct > 15 && (
                                                    <span className="text-white text-xs font-bold">{(step.count || 0).toLocaleString('id-ID')}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-400">No funnel data available</div>
                    )}
                </CardContent>
            </Card>

            {/* ═══ SECTION 6: KEY EVENTS ═══ */}
            <Card className="border-0 shadow-md">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <MousePointerClick className="w-5 h-5 text-indigo-600" />
                        Key Events
                    </CardTitle>
                    <CardDescription>Important user interactions tracked on the website</CardDescription>
                </CardHeader>
                <CardContent>
                    {(events.key_events || []).length > 0 ? (
                        <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height={320}>
                            <BarChart
                                data={(events.key_events || []).map(e => ({
                                    ...e,
                                    displayName: (e.eventName || '')
                                        .replace(/_/g, ' ')
                                        .replace(/\b\w/g, l => l.toUpperCase())
                                }))}
                                layout="vertical"
                                margin={{ left: 20, right: 30 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                                <YAxis
                                    dataKey="displayName" type="category" width={130}
                                    tick={{ fontSize: 11, fill: '#4b5563' }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="eventCount" name="Count" radius={[0, 6, 6, 0]} barSize={24}>
                                    {(events.key_events || []).map((_, i) => (
                                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="text-center py-12 text-gray-400">No event data available</div>
                    )}
                </CardContent>
            </Card>

            {/* ═══ SECTION 7 + 8: DEVICES & LOCATIONS ═══ */}
            <div className="grid gap-4 lg:grid-cols-2">
                {/* Devices */}
                <Card className="border-0 shadow-md">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Monitor className="w-5 h-5 text-teal-600" />
                            Device Performance
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {devices.length > 0 ? (
                            <div className="space-y-4">
                                <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height={200}>
                                    <PieChart>
                                        <Pie
                                            data={devices.map(d => ({ name: (d.deviceCategory || '').charAt(0).toUpperCase() + (d.deviceCategory || '').slice(1), value: d.sessions || 0 }))}
                                            cx="50%" cy="50%"
                                            innerRadius={50} outerRadius={85}
                                            paddingAngle={4} dataKey="value" stroke="none"
                                        >
                                            {devices.map((_, i) => (
                                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="space-y-2">
                                    {devices.map((dev, i) => {
                                        const DevIcon = dev.deviceCategory === 'mobile' ? Smartphone : dev.deviceCategory === 'tablet' ? Tablet : Monitor;
                                        return (
                                            <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <DevIcon className="w-4 h-4 text-gray-500" />
                                                    <span className="text-sm font-medium text-gray-700 capitalize">{dev.deviceCategory}</span>
                                                </div>
                                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                                    <span>{(dev.sessions || 0).toLocaleString('id-ID')} sessions</span>
                                                    <span className="text-emerald-600 font-semibold">{dev.engagement_rate || 0}% eng</span>
                                                    <span className={`${(dev.bounce_rate || 0) > 60 ? 'text-red-500' : 'text-gray-500'}`}>{dev.bounce_rate || 0}% bounce</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-400">No device data</div>
                        )}
                    </CardContent>
                </Card>

                {/* Locations */}
                <Card className="border-0 shadow-md">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <MapPin className="w-5 h-5 text-rose-600" />
                            Location Performance
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {(location.cities || []).length > 0 ? (
                            <div>
                                <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height={280}>
                                    <BarChart data={(location.cities || []).slice(0, 10)} layout="vertical" margin={{ left: 10, right: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                                        <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} hide />
                                        <YAxis dataKey="city" type="category" width={100} tick={{ fontSize: 11, fill: '#4b5563' }} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="sessions" name="Sessions" radius={[0, 6, 6, 0]} barSize={20}>
                                            {(location.cities || []).slice(0, 10).map((_, i) => (
                                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>

                                {/* Country summary */}
                                {(location.countries || []).length > 0 && (
                                    <div className="mt-4 pt-4 border-t">
                                        <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Top Countries</p>
                                        <div className="flex flex-wrap gap-2">
                                            {(location.countries || []).slice(0, 6).map((c, i) => (
                                                <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-50 rounded-full text-xs text-gray-600">
                                                    <Globe className="w-3 h-3" />
                                                    {c.country}: <strong>{(c.sessions || 0).toLocaleString('id-ID')}</strong>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-400">No location data</div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* ═══ SECTION 9: CLARITY UX METRICS ═══ */}
            <Card className="border-0 shadow-md">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <Zap className="w-5 h-5 text-amber-600" />
                        UX Metrics
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2">Microsoft Clarity</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {clarity && clarity.configured ? (
                        clarity.api_limited ? (
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                                <Info className="w-8 h-8 text-blue-500 mx-auto mb-3" />
                                <p className="text-blue-700 font-medium mb-2">Clarity API access limited</p>
                                <p className="text-blue-600 text-sm mb-4">{clarity.message}</p>
                                {clarity.dashboard_url && (
                                    <a href={clarity.dashboard_url} target="_blank" rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium">
                                        <ExternalLink className="w-4 h-4" /> Open Clarity Dashboard
                                    </a>
                                )}
                            </div>
                        ) : (
                            <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                                {[
                                    { label: 'Dead Clicks', value: clarity.dead_clicks, color: 'text-amber-600', bg: 'bg-amber-50' },
                                    { label: 'Rage Clicks', value: clarity.rage_clicks, color: 'text-red-600', bg: 'bg-red-50' },
                                    { label: 'Excessive Scrolling', value: clarity.excessive_scrolling, color: 'text-purple-600', bg: 'bg-purple-50' },
                                    { label: 'Quick Backs', value: clarity.quick_backs, color: 'text-blue-600', bg: 'bg-blue-50' },
                                ].map((m, i) => (
                                    <div key={i} className={`${m.bg} rounded-xl p-5 text-center`}>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{m.label}</p>
                                        <p className={`text-3xl font-bold mt-2 ${m.color}`}>{(m.value || 0).toLocaleString('id-ID')}</p>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                            <Info className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 font-medium mb-1">Microsoft Clarity not configured</p>
                            <p className="text-gray-400 text-sm">Set <code className="bg-gray-200 px-1 rounded">CLARITY_PROJECT_ID</code> and <code className="bg-gray-200 px-1 rounded">CLARITY_API_TOKEN</code> in your .env file</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ═══ SECTION 10 + 11: AI EXECUTIVE SUMMARY & RECOMMENDATIONS ═══ */}
            <Card className="border-0 shadow-md overflow-hidden">
                <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 px-6 py-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/15 rounded-xl backdrop-blur-sm">
                                <Sparkles className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">AI Executive Summary & Recommendations</h3>
                                <p className="text-purple-200 text-sm">Powered by AI — Automated analysis of your website performance</p>
                            </div>
                        </div>
                        <button
                            onClick={fetchAISummary}
                            disabled={aiLoading}
                            className="flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white px-4 py-2 rounded-lg text-sm transition-all border border-white/20 disabled:opacity-50"
                        >
                            {aiLoading ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                            ) : (
                                <>{aiData ? <RefreshCw className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />} {aiData ? 'Regenerate' : 'Generate Summary'}</>
                            )}
                        </button>
                    </div>
                </div>

                <CardContent className="p-6">
                    {aiLoading ? (
                        <div className="flex flex-col items-center justify-center py-16 space-y-4">
                            <div className="relative">
                                <div className="w-16 h-16 border-4 border-purple-200 rounded-full animate-spin border-t-purple-600" />
                                <Sparkles className="w-6 h-6 text-purple-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                            </div>
                            <p className="text-gray-500 font-medium animate-pulse">AI is analyzing your data...</p>
                            <p className="text-gray-400 text-sm">This may take 15-30 seconds</p>
                        </div>
                    ) : aiData ? (
                        <div className="space-y-8">
                            {/* Executive Summary Sections */}
                            {aiData.summary?.available && aiData.summary.sections && (
                                <div>
                                    <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-purple-600" />
                                        Executive Summary
                                    </h4>
                                    <div className="grid gap-3 md:grid-cols-2">
                                        {(aiData.summary.sections || []).map((sec, idx) => (
                                            <div
                                                key={idx}
                                                className="border border-gray-100 rounded-xl p-4 hover:border-purple-200 hover:shadow-sm transition-all cursor-pointer"
                                                onClick={() => toggleSection(idx)}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex items-start gap-3 flex-1">
                                                        <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center text-purple-700 text-xs font-bold flex-shrink-0 mt-0.5">
                                                            {idx + 1}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-semibold text-gray-800 text-sm">{sec.title}</p>
                                                            <p className={`text-gray-600 text-sm mt-1 leading-relaxed ${expandedSections[idx] ? '' : 'line-clamp-3'}`}>
                                                                {sec.content}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {expandedSections[idx]
                                                        ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                        : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                    }
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* AI Recommendations */}
                            {aiData.recommendations?.available && (
                                <div>
                                    <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                                        AI Recommendations
                                    </h4>
                                    <div className="space-y-3">
                                        {(aiData.recommendations.recommendations || []).map((rec, idx) => (
                                            <div key={idx} className="border border-gray-100 rounded-xl p-4 hover:border-emerald-200 hover:shadow-sm transition-all">
                                                <div className="flex items-start gap-3">
                                                    <div className="flex-shrink-0 mt-0.5">
                                                        <PriorityBadge priority={rec.priority} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <p className="font-semibold text-gray-800 text-sm">{rec.title}</p>
                                                            {rec.category && (
                                                                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{rec.category}</span>
                                                            )}
                                                            {rec.timeline && (
                                                                <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">{rec.timeline}</span>
                                                            )}
                                                        </div>
                                                        <p className="text-gray-600 text-sm mt-1">{rec.description}</p>
                                                        {rec.action_items && rec.action_items.length > 0 && (
                                                            <ul className="mt-2 space-y-1">
                                                                {rec.action_items.map((item, j) => (
                                                                    <li key={j} className="text-xs text-gray-500 flex items-start gap-1.5">
                                                                        <span className="text-emerald-500 mt-0.5">▸</span>
                                                                        {item}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                        {rec.expected_impact && (
                                                            <p className="text-xs text-emerald-600 mt-2 font-medium">
                                                                💡 Expected: {rec.expected_impact}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Error states */}
                            {aiData.summary && !aiData.summary.available && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                                    <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                                    <p className="text-amber-700 font-medium">{aiData.summary.message || aiData.summary.error || 'AI summary not available'}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <div className="w-20 h-20 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Sparkles className="w-10 h-10 text-purple-300" />
                            </div>
                            <p className="text-gray-500 font-medium mb-2">AI Summary not generated yet</p>
                            <p className="text-gray-400 text-sm mb-6">Click "Generate Summary" above to get AI-powered insights from your analytics data</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ═══ FOOTER ═══ */}
            <div className="text-center text-xs text-gray-400 pt-4 pb-8">
                <p>Spencer Green Hotel — Automated Website Performance Report</p>
                <p className="mt-1">Data sourced from Google Analytics 4 • Generated {new Date().toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
        </div>
    );
};

export default PerformanceReport;
